import React, { useMemo, useState } from 'react';
import { ui } from './canvasUi';

/**
 * A pivot block's card body: the pivoted MATRIX the server computed, rendered
 * as a plain table, with a pager for tall pivots.
 *
 * WHY THIS RENDERS result.matrix AND NOT react-pivottable
 *
 * The drag-and-drop component (`PivotTableUI`) lives in the settings drawer,
 * where rearranging belongs. Rendering it here too would mean every card on a
 * dashboard mounting a sortable/draggable widget, and — more importantly — the
 * card would then be showing a pivot computed in the browser while the email
 * and the exports show one computed on the server. Rendering the same stored
 * `matrix` every other surface renders is what makes "the deck matches the
 * dashboard" true rather than nearly true.
 *
 * It also means a shared/public canvas needs no pivot library at all: the
 * matrix is just a 2D array, and `result.rows` is deliberately withheld from
 * link-holders (see sanitizeCanvasForPublic).
 */

const PAGE_SIZE = 25;

const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, height: '100%' },
    scroll: { overflow: 'auto', minHeight: 0, flex: 1 },
    table: { borderCollapse: 'collapse', width: '100%', fontSize: '12px', fontVariantNumeric: 'tabular-nums' },
    th: {
        border: `1px solid ${ui.hairline}`, padding: '5px 8px', textAlign: 'left',
        fontSize: '10.5px', fontWeight: 700, color: ui.ink2,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        background: ui.paper, position: 'sticky', top: 0, zIndex: 1,
        whiteSpace: 'nowrap',
    },
    td: { border: `1px solid ${ui.hairline}`, padding: '5px 8px', color: ui.ink },
    tdNum: { textAlign: 'right' },
    tdLabel: { fontWeight: 600, whiteSpace: 'nowrap' },
    totalRow: { background: '#fafbfc', fontWeight: 700 },
    bar: {
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px',
        fontSize: '11.5px', color: ui.ink2, fontVariantNumeric: 'tabular-nums', flexShrink: 0,
    },
    btn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '26px', height: '24px', padding: '0 8px',
        borderRadius: ui.radiusSm, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: '11.5px', fontWeight: 600,
    },
    btnOff: { opacity: 0.4, cursor: 'default' },
    count: { marginRight: 'auto' },
    note: { fontSize: '11px', color: ui.ink2, flexShrink: 0 },
    empty: {
        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: ui.ink2, fontSize: '12.5px', textAlign: 'center', padding: '12px',
    },
};

const fmt = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString() : (v ?? ''));

export default function PivotBlockBody({ block, r }) {
    const matrix = useMemo(() => (Array.isArray(r?.matrix) ? r.matrix.filter(Array.isArray) : []), [r]);
    const [page, setPage] = useState(0);

    // A pivot with no fields arranged yet is a legitimate state, but it must
    // NOT render as a table: pivotMatrix always emits a header row plus a
    // grand-total row, so an unarranged pivot comes back as
    // [["Sum","Total"],["Total",0]] — length 2, and a card confidently
    // reporting that the user's data totals 0. Detect it from the
    // ARRANGEMENT, not the matrix length.
    //
    // `vals` alone is enough to be a real pivot (a single grand-total figure
    // is a legitimate, scorecard-like result), so the nudge only shows when
    // nothing at all has been chosen.
    const p = block?.config?.pivot || {};
    const unarranged = [p.rows, p.cols, p.vals].every((f) => !Array.isArray(f) || f.length === 0);
    if (unarranged || matrix.length < 2) {
        return <div style={s.empty}>Open settings to choose what this pivot breaks down by.</div>;
    }

    const [header, ...body] = matrix;
    // The server always appends a grand-total row last; it should not paginate
    // away, so it is pulled out and pinned under whichever page is showing.
    const totalRow = body.length > 0 ? body[body.length - 1] : null;
    const dataRows = body.slice(0, -1);

    const pages = Math.max(1, Math.ceil(dataRows.length / PAGE_SIZE));
    const safePage = Math.min(page, pages - 1);
    const start = safePage * PAGE_SIZE;
    const shown = dataRows.slice(start, start + PAGE_SIZE);
    const paged = dataRows.length > PAGE_SIZE;

    const cellStyle = (v, i) => ({
        ...s.td,
        ...(i === 0 ? s.tdLabel : (typeof v === 'number' ? s.tdNum : {})),
    });

    return (
        <div style={s.wrap} data-testid="canvas-pivot-body">
            <div style={s.scroll}>
                <table style={s.table}>
                    <thead>
                        <tr>{header.map((h, i) => <th key={i} style={s.th}>{fmt(h)}</th>)}</tr>
                    </thead>
                    <tbody>
                        {shown.map((row, ri) => (
                            <tr key={start + ri}>
                                {header.map((_, ci) => (
                                    <td key={ci} style={cellStyle(row[ci], ci)}>{fmt(row[ci])}</td>
                                ))}
                            </tr>
                        ))}
                        {totalRow && (
                            <tr style={s.totalRow}>
                                {header.map((_, ci) => (
                                    <td key={ci} style={cellStyle(totalRow[ci], ci)}>{fmt(totalRow[ci])}</td>
                                ))}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {/* Truncation is stated, never silent: a pivot computed over the
                first 2,000 rows of a larger source is a different number from
                the one the reader assumes they are looking at. */}
            {r.truncated && (
                <div style={s.note} data-testid="canvas-pivot-truncated">
                    Pivoted the first {Number(r.computedOverRows || 0).toLocaleString()} of{' '}
                    {Number(r.sourceRows || 0).toLocaleString()} rows — add filters to narrow the source.
                </div>
            )}
            {/* Distinct from source-row truncation above, and it matters more:
                the totals are computed over EVERY key, so a trimmed matrix
                shows rows that visibly don't sum to their own Total. Saying so
                is the difference between a caveat and a wrong number. */}
            {(r.droppedRowKeys > 0 || r.droppedColKeys > 0) && (
                <div style={s.note} data-testid="canvas-pivot-keys-truncated">
                    Showing the first{' '}
                    {[
                        r.droppedRowKeys > 0 ? `${dataRows.length.toLocaleString()} of ${(dataRows.length + r.droppedRowKeys).toLocaleString()} rows` : null,
                        r.droppedColKeys > 0 ? `${(header.length - 2).toLocaleString()} of ${(header.length - 2 + r.droppedColKeys).toLocaleString()} columns` : null,
                    ].filter(Boolean).join(' and ')}
                    . Totals cover everything.
                </div>
            )}
            {paged && (
                <div style={s.bar}>
                    <span style={s.count}>
                        {start + 1}–{start + shown.length} of {dataRows.length}
                    </span>
                    <button type="button" className="cvx-ghost cvx-focus"
                        style={{ ...s.btn, ...(safePage === 0 ? s.btnOff : {}) }}
                        onClick={() => setPage(safePage - 1)} disabled={safePage === 0}
                        aria-label="Previous page">‹</button>
                    <span aria-live="polite">{safePage + 1} / {pages}</span>
                    <button type="button" className="cvx-ghost cvx-focus"
                        style={{ ...s.btn, ...(safePage >= pages - 1 ? s.btnOff : {}) }}
                        onClick={() => setPage(safePage + 1)} disabled={safePage >= pages - 1}
                        aria-label="Next page">›</button>
                </div>
            )}
        </div>
    );
}
